<template>
  <div class="item-app-usage" @click="itemClick">
    <div class="top">
      <div class="avatar">
        <child-avatar
          size="small"
          :child="child"
          :show-status="false" />
      </div>

      <div class="title">
        <div>{{ summaryTitle }}</div>
        <div>{{ summaryDates }}</div>
      </div>
    </div>

    <div class="description">
      <div class="text">
        {{ item.details.explanation_text }}
      </div>

      <div class="toggle-button">
        <base-button
          color="light"
          :loading="loadingApps"
          @click="toggleApps">
          {{ toggleButtonText }}
        </base-button>
      </div>
    </div>

    <div v-if="showApps">
      <ul class="list">
        <li v-for="app in itemApps"
            class="list-item list-item-no-border list-item-sm"
            :key="app.id">
          <app-settings-item :app="app"
                             :child-id="child ? child.id : ''"
                             :disabled="!child"
                             @changeAction="changeAction" />
        </li>
      </ul>

      <div v-if="item.user"
           class="go-to-app-settings"
           @click="openAppSettingsModal">
        <div class="title">
          {{ $t("feed.items.appUsageItemRedirect") }}
        </div>

        <div class="arrow-right"></div>
      </div>
    </div>
  </div>
</template>

<script>
  import ItemBase from '@/components/Feed/Items/ItemBase.vue'
  import AppSettingsItem from '@/components/Child/ChildAppSettings/AppSettingsItem.vue'

  import { isDatesEquals } from '@/services/intl'

  import analytics from '@/services/analytics.js'

  export default {
    extends: ItemBase,

    components: {
      AppSettingsItem
    },

    props: {
      item: {
        required: true,
        type: Object
      }
    },

    data() {
      return {
        showApps: false,
        loadingApps: false,
        appSettings: [],
        appDetails: []
      }
    },

    created() {
      if (this.child) {
        this.loadAppsData()
      }
    },

    methods: {
      ...mapActions('children', {
        loadAppSettings: 'loadAppSettings',
        updateAppSettings: 'updateAppSettings'
      }),

      changeAction(app) {
        const { id, action } = app

        this.updateAppSettings({
          user_id: this.item.user_id,
          mode_id: this.standardModeId,
          app_id: id,
          action
        })
      },

      toggleApps() {
        if (this.showApps) {
          this.showApps = false
        } else {

          if (!this.child) {
            this.showApps = true
            return
          }

          this.loadingApps = true

          this.loadAppsData().then(() => {
            this.loadingApps = false
            this.showApps = true
          })
        }
      },

      async loadAppsData() {
        await this.loadAppSettings({
          user_id: this.item.user_id,
          mode_id: this.standardModeId
        })
      },

      openAppSettingsModal() {
        if (!this.currentUser.hasPaidLicense) {
          return
        }

        this.showModal('apps', {
          childId: this.item.user_id,
          modeId:  this.standardModeId
        })
      },

      itemClick() {
        analytics.sendEvent('Pk_AppUsage_Feed')
      }
    },

    computed: {
      ...mapGetters('children', {
        getChildById: 'getById'
      }),

      child() {
        return this.getChildById(this.item.user_id)
      },

      standardModeId() {
        return this.child
          ? this.child.modes.find(mode => mode.name === 'Standard').id
          : ''
      },

      summaryTitle() {
        return this.child
          ? this.$t("feed.items.appUsageItemTitle", { name: this.child.name })
          : this.$t("feed.items.appUsageItemRemoved")
      },

      summaryDates() {
        const from = this.$options.filters.localDateTimeMedium(this.item.details.begin_time)
        const to = this.$options.filters.localDateTimeMedium(this.item.details.end_time)
        const datesEqual = isDatesEquals(this.item.details.begin_time, this.item.details.end_time)

        if (datesEqual) {
          return `${from} - ${this.$options.filters.localTime(this.item.details.end_time)}`
        }

        return `${from} - ${to}`
      },

      itemApps() {
        return this.item.details.applications
      },

      toggleButtonText() {
        return this.showApps
          ? this.$t("hide")
          : this.$t("show")
      },

      ...mapGetters('auth', {
        currentUser: 'currentUser'
      })
    }
  }
</script>

<style lang="scss" scoped>
  .item-app-usage {
    .top {
      display: flex;

      & > .title {
        display: flex;
        flex-direction: column;
        margin-left: 10px;
        overflow: hidden;
        color: #3e3a3b;
        font-size: 16px;
      }
    }

    .description {
      display: flex;
      align-items: center;
      margin-top: 8px;

      .text {
        width: 220px;
        color: #3e3a3b;
        font-size: 14px;
        line-height: 1.5;
        opacity: 0.5;
        overflow: hidden;
      }

      .toggle-button {
        margin-left: auto;
        flex: 0 0 75px;
      }
    }

    .app-list {
      list-style-type: none;
      margin: 0 0 20px 0;
      padding: 0 0 10px 0;
      border-bottom: 1px solid #ebebeb;
    }

    .app-item {
      display: flex;
      align-items: center;
      padding: 10px 0;

      .picture {
        width: 30px;
        height: 30px;
      }

      .label {
        margin-left: 10px;
        border-radius: 100px;
      }

      .control-buttons {
        display: flex;
        margin-left: auto;
      }
    }

    .go-to-app-settings {
      display: flex;
      justify-content: space-between;
      font-size: 16px;
      font-weight: normal;

      padding: 10px 0 5px 0;

      color: #3e3a3b;
      cursor: pointer;
    }

    .arrow-right {
      width: 13px;
      height: 13px;
      margin-right: 2px;
      border-bottom: 1px solid #b9b9b9;
      border-right: 1px solid #b9b9b9;
      transform: rotate(-45deg);
    }
  }
</style>
